<script lang="ts">
	import { __, sprintf } from '@wordpress/i18n';
	import type {
		StaffelprijsItem,
		SpoedleveringOptions,
		DeliveryOption,
	} from '../../../types';

	type Props = {
		tiers: StaffelprijsItem[];
		spoedleveringOptions: SpoedleveringOptions | null;
		levertijdDagen: number;
		selectedQuantity: number;
		selectedDruktype: string;
		selectedDeliveryOption: DeliveryOption;
		showInclTax: boolean;
		taxRate: number;
		hasSpoedInVariation: boolean;
		// Custom quantity
		useCustomQuantity: boolean;
		customQuantity: number;
		customQuantityError?: string;
		disableCustomQuantity?: boolean;
		// Callbacks
		onSelectCell: (quantity: number, deliveryOption: DeliveryOption, druktype: string) => void;
		onToggleCustom: () => void;
		onCustomQuantityChange: (value: number) => void;
		onSelectDeliveryForCustom: (option: DeliveryOption) => void;
	};

	let {
		tiers,
		spoedleveringOptions,
		levertijdDagen,
		selectedQuantity,
		selectedDruktype,
		selectedDeliveryOption,
		showInclTax,
		taxRate,
		hasSpoedInVariation,
		useCustomQuantity,
		customQuantity,
		customQuantityError,
		disableCustomQuantity = false,
		onSelectCell,
		onToggleCustom,
		onCustomQuantityChange,
		onSelectDeliveryForCustom,
	}: Props = $props();

	// Ordered spoed options (8d = cheapest first, then 5d, then 3d)
	const spoedOrder: { key: DeliveryOption; tierFlag: 'spoed_8' | 'spoed_5' | 'spoed_3'; settingsKey: keyof SpoedleveringOptions }[] = [
		{ key: 'spoed_8', tierFlag: 'spoed_8', settingsKey: '8_dagen' },
		{ key: 'spoed_5', tierFlag: 'spoed_5', settingsKey: '5_dagen' },
		{ key: 'spoed_3', tierFlag: 'spoed_3', settingsKey: '3_dagen' },
	];

	/**
	 * Add working days to a date (excluding weekends)
	 */
	function addWorkingDays(startDate: Date, workingDays: number): Date {
		const result = new Date(startDate);
		let daysAdded = 0;
		while (daysAdded < workingDays) {
			result.setDate(result.getDate() + 1);
			if (result.getDay() !== 0 && result.getDay() !== 6) {
				daysAdded++;
			}
		}
		return result;
	}

	/**
	 * Format a date as "vr 7 feb" style
	 */
	function formatDateShort(days: number): string {
		const date = addWorkingDays(new Date(), days);
		const dayNames = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
		const monthNames = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
		return `${dayNames[date.getDay()]} ${date.getDate()} ${monthNames[date.getMonth()]}`;
	}

	/**
	 * Format price to EUR
	 */
	function formatPrice(price: number): string {
		return new Intl.NumberFormat('nl-NL', {
			style: 'currency',
			currency: 'EUR',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(price);
	}

	// Round up to nearest 0.05 (matches cart/checkout rounding)
	function roundUpTo005(value: number): number {
		return Math.ceil(Math.round(value * 20 * 10000) / 10000) / 20;
	}

	/**
	 * Calculate total price for a tier (with optional surcharge), applying tax if needed
	 */
	function calcTotal(tier: StaffelprijsItem, surchargePercent: number = 0): number {
		let total = tier.prijs * tier.aantal;
		if (surchargePercent > 0) {
			total = total * (1 + surchargePercent / 100);
		}
		total = roundUpTo005(total);
		if (showInclTax) {
			total = total * (1 + taxRate);
		}
		return total;
	}

	const taxLabel = $derived(showInclTax ? __('Incl. btw', 'wpc') : __('Excl. btw', 'wpc'));

	/**
	 * Check if a cell is selected
	 */
	function isCellSelected(tierAantal: number, tierDruktype: string, option: DeliveryOption): boolean {
		if (useCustomQuantity) return false;
		if (selectedQuantity !== tierAantal) return false;
		if (selectedDruktype && tierDruktype && selectedDruktype !== tierDruktype) return false;
		return selectedDeliveryOption === option;
	}

	/**
	 * Handle custom input change
	 */
	function handleCustomInput(event: Event) {
		const input = event.target as HTMLInputElement;
		const value = parseInt(input.value) || 0;
		onCustomQuantityChange(value);
	}

	const spoedColumns = $derived.by(() => {
		if (!hasSpoedInVariation || !spoedleveringOptions) return [];
		const cols: { key: DeliveryOption; tierFlag: 'spoed_8' | 'spoed_5' | 'spoed_3'; surcharge: number; days: number }[] = [];
		for (const { key, tierFlag, settingsKey } of spoedOrder) {
			const opt = spoedleveringOptions[settingsKey];
			if (opt?.enabled) {
				cols.push({ key, tierFlag, surcharge: opt.surcharge, days: opt.days });
			}
		}
		return cols;
	});

	/**
	 * Determine which druktype group a custom quantity falls into.
	 * Returns the slice of tiers that belong to the detected group,
	 * or all tiers when no druktype split is present.
	 */
	const customTierGroup = $derived.by((): StaffelprijsItem[] | null => {
		if (!useCustomQuantity || customQuantity <= 0 || customQuantityError) return null;
		const firstOffsetIndex = tiers.findIndex((t) => t.druktype === 'offset');
		if (firstOffsetIndex === -1) return tiers;
		return customQuantity < tiers[firstOffsetIndex].aantal
			? tiers.slice(0, firstOffsetIndex)
			: tiers.slice(firstOffsetIndex);
	});

	/**
	 * Within the detected group, the applicable tier is the largest one
	 * whose aantal <= customQuantity (floor match).
	 */
	const customApplicableTier = $derived.by((): StaffelprijsItem | null => {
		const group = customTierGroup;
		if (!group || group.length === 0) return null;
		const sorted = [...group].sort((a, b) => b.aantal - a.aantal);
		return sorted.find((t) => customQuantity >= t.aantal) ?? sorted[sorted.length - 1];
	});
</script>

{#if tiers.length > 0}
	<div class="opl-table-wrapper">
		<table class="opl-table">
			<thead>
				<tr>
					<th class="opl-table__header opl-table__header--oplage">
						{__('Oplage', 'wpc')}
					</th>
					<th class="opl-table__header opl-table__header--normaal">
						<span class="opl-badge opl-badge--normaal">{__('Standaard', 'wpc')} {tiers[0]?.levertijd_dagen || levertijdDagen} {__('werkdagen', 'wpc')}</span>
					</th>
					{#each spoedColumns as col (col.key)}
						<th class="opl-table__header opl-table__header--spoed">
							<span class="opl-badge opl-badge--spoed">Spoed {col.days} {__('werkdagen', 'wpc')}</span>
						</th>
					{/each}
				</tr>
			</thead>
			<tbody>
				{#each tiers as tier, i (`${tier.aantal}-${tier.druktype ?? ''}`)}
					{@const normalTotal = calcTotal(tier)}
					{#if i > 0 && tier.druktype && tiers[i-1].druktype && tiers[i-1].druktype !== tier.druktype}
						<tr class="opl-group-header-row">
							<td class="opl-table__header opl-table__header--oplage">{__("Oplage", "wpc")}</td>
							<td class="opl-table__header opl-table__header--normaal">
								<span class="opl-badge opl-badge--normaal">{__("Standaard", "wpc")} {tier.levertijd_dagen || levertijdDagen} {__("werkdagen", "wpc")}</span>
							</td>
							{#each spoedColumns as col (col.key)}
								<td class="opl-table__header opl-table__header--spoed">
									<span class="opl-badge opl-badge--spoed">Spoed {col.days} {__("werkdagen", "wpc")}</span>
								</td>
							{/each}
						</tr>
					{/if}
					<tr>
						<td class="opl-table__cell opl-table__cell--oplage">
							{tier.aantal.toLocaleString('nl-NL')}
						</td>
						<td
							class="opl-table__cell opl-table__cell--price"
							class:opl-table__cell--selected={isCellSelected(tier.aantal, tier.druktype ?? '', 'normaal')}
							role="button"
							tabindex="0"
							onclick={() => onSelectCell(tier.aantal, 'normaal', tier.druktype ?? '')}
							onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectCell(tier.aantal, 'normaal', tier.druktype ?? ''); } }}
						>
							<span class="opl-price">{formatPrice(normalTotal)}</span>
							<span class="opl-tax-label">{taxLabel}</span>
						</td>
						{#each spoedColumns as col (col.key)}
							{@const spoedTotal = tier[col.tierFlag] ? calcTotal(tier, col.surcharge) : null}
							<td
								class="opl-table__cell opl-table__cell--price"
								class:opl-table__cell--selected={isCellSelected(tier.aantal, tier.druktype ?? '', col.key)}
								class:opl-table__cell--disabled={!tier[col.tierFlag]}
								role="button"
								tabindex={tier[col.tierFlag] ? 0 : -1}
								onclick={() => { if (tier[col.tierFlag]) onSelectCell(tier.aantal, col.key, tier.druktype ?? ''); }}
								onkeydown={(e) => { if (tier[col.tierFlag] && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onSelectCell(tier.aantal, col.key, tier.druktype ?? ''); } }}
							>
								{#if spoedTotal !== null}
									<span class="opl-price">{formatPrice(spoedTotal)}</span>
									<span class="opl-tax-label">{taxLabel}</span>
								{:else}
									<span class="opl-unavailable">&mdash;</span>
								{/if}
							</td>
						{/each}
					</tr>
				{/each}
			</tbody>
		</table>
	</div>

	<!-- Custom quantity input -->
	{#if !disableCustomQuantity}
	<div class="wpc-custom-quantity">
		<label class="wpc-custom-quantity__label">
			<input
				type="checkbox"
				checked={useCustomQuantity}
				onchange={onToggleCustom}
				class="wpc-radio"
				style="display: inline-block; margin-right: 8px; vertical-align: middle;"
			/>
			{__('Of kies een ander aantal', 'wpc')}
		</label>

		{#if useCustomQuantity}
			<div class="wpc-mt-4">
				<input
					type="number"
					class="wpc-input {customQuantityError ? 'wpc-input--error' : ''}"
					value={customQuantity || ''}
					oninput={handleCustomInput}
					placeholder={__('bijv. 1500', 'wpc')}
					min="0"
					step="1"
				/>
				{#if customQuantityError}
					<p class="wpc-custom-quantity__error">
						&#10007; {customQuantityError}
					</p>
				{/if}
			</div>

			{#if customApplicableTier && !customQuantityError}
				<div class="opl-custom-delivery">
					<button
						type="button"
						class="opl-custom-delivery__option"
						class:opl-custom-delivery__option--selected={selectedDeliveryOption === 'normaal'}
						onclick={() => onSelectDeliveryForCustom('normaal')}
					>
						<span class="opl-badge opl-badge--normaal">{__('Standaard', 'wpc')} {customApplicableTier.levertijd_dagen || levertijdDagen} {__('werkdagen', 'wpc')}</span>
					</button>
					{#each spoedColumns as col (col.key)}
						{#if customApplicableTier[col.tierFlag]}
							<button
								type="button"
								class="opl-custom-delivery__option"
								class:opl-custom-delivery__option--selected={selectedDeliveryOption === col.key}
								onclick={() => onSelectDeliveryForCustom(col.key)}
							>
								<span class="opl-badge opl-badge--spoed">Spoed {col.days} {__('werkdagen', 'wpc')}</span>
							</button>
						{/if}
					{/each}
				</div>
			{/if}
		{/if}
	</div>
	{/if}
{:else}
	<!-- No tiers available, show custom input only -->
	<div>
		<label for="custom-quantity-input" class="wpc-custom-quantity__label">{__('Kies aantal', 'wpc')}</label>
		<input
			id="custom-quantity-input"
			type="number"
			class="wpc-input {customQuantityError ? 'wpc-input--error' : ''}"
			value={customQuantity || ''}
			oninput={handleCustomInput}
			placeholder={__('Voer aantal in', 'wpc')}
			min="0"
			step="1"
		/>
		{#if customQuantityError}
			<p class="wpc-custom-quantity__error">
				&#10007; {customQuantityError}
			</p>
		{/if}
	</div>
{/if}

<style>
	.opl-table-wrapper {
		overflow-x: auto;
		-webkit-overflow-scrolling: touch;
	}

	.opl-table {
		width: 100%;
		border-collapse: collapse;
		border: 1px solid var(--wpc-gray-200);
		border-radius: var(--wpc-radius-md);
		overflow: hidden;
		font-size: var(--wpc-font-size-sm);
	}

	.opl-table__header {
		padding: var(--wpc-space-sm) var(--wpc-space-md);
		background: var(--wpc-gray-50);
		border-bottom: 2px solid var(--wpc-gray-200);
		text-align: center;
		font-weight: var(--wpc-font-weight-semibold);
		white-space: nowrap;
	}

	.opl-table__header--oplage {
		text-align: left;
		width: 80px;
	}

	.opl-table__header--normaal,
	.opl-table__header--spoed {
		min-width: 130px;
	}

	.opl-header-date {
		display: block;
		font-size: var(--wpc-font-size-xs);
		font-weight: var(--wpc-font-weight-normal);
		color: var(--wpc-gray-600);
		margin-top: 2px;
	}

	.opl-badge {
		display: inline-block;
		padding: 2px 10px;
		border-radius: var(--wpc-radius-full);
		font-size: var(--wpc-font-size-xs);
		font-weight: var(--wpc-font-weight-semibold);
		letter-spacing: 0.3px;
	}

	.opl-badge--normaal {
		background: var(--wpc-success-light);
		color: #065f46;
	}

	.opl-badge--spoed {
		background: #fef3c7;
		color: #92400e;
	}

	.opl-table__cell {
		padding: var(--wpc-space-sm) var(--wpc-space-md);
		border-bottom: 1px solid var(--wpc-gray-200);
		text-align: center;
		vertical-align: middle;
	}

	.opl-table__cell--oplage {
		text-align: left;
		font-weight: var(--wpc-font-weight-semibold);
		color: var(--wpc-gray-900);
	}

	.opl-table__cell--price {
		cursor: pointer;
		transition: all var(--wpc-transition-fast);
		user-select: none;
	}

	.opl-table__cell--price:hover:not(.opl-table__cell--disabled) {
		background: var(--wpc-gray-50);
	}

	.opl-table__cell--selected {
		background: #eff6ff !important;
		outline: 2px solid var(--wpc-accent);
		outline-offset: -1px;
		font-weight: var(--wpc-font-weight-semibold);
	}

	.opl-table__cell--disabled {
		cursor: default;
		color: var(--wpc-gray-300);
	}

	.opl-price {
		display: block;
		font-weight: var(--wpc-font-weight-semibold);
		color: var(--wpc-gray-900);
		font-size: var(--wpc-font-size-base);
	}

	.opl-tax-label {
		display: block;
		font-size: var(--wpc-font-size-xs);
		color: var(--wpc-gray-600);
		font-weight: var(--wpc-font-weight-normal);
	}

	.opl-unavailable {
		color: var(--wpc-gray-300);
		font-size: var(--wpc-font-size-lg);
	}

	/* Last row: no bottom border */
	.opl-table tbody tr:last-child .opl-table__cell {
		border-bottom: none;
	}

	.opl-group-header-row td {
		border-top: 2px solid var(--wpc-gray-300);
	}

	.opl-custom-delivery {
		display: flex;
		flex-wrap: wrap;
		gap: var(--wpc-space-sm);
		margin-top: var(--wpc-space-md);
	}

	.opl-custom-delivery__option {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
		padding: var(--wpc-space-sm) var(--wpc-space-md);
		border: 1px solid var(--wpc-gray-200);
		border-radius: var(--wpc-radius-md);
		background: white;
		cursor: pointer;
		transition: all var(--wpc-transition-fast);
		min-width: 110px;
	}

	.opl-custom-delivery__option:hover {
		background: var(--wpc-gray-50);
	}

	.opl-custom-delivery__option--selected {
		background: #eff6ff !important;
		outline: 2px solid var(--wpc-accent);
		outline-offset: -1px;
	}
</style>
